import React, { useEffect, useMemo, useRef } from "react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ScrollArea,
  Skeleton,
} from "@lemonadetraining/web-ui";
import { motion } from "motion/react";
import { IconBolt, IconTrophy, IconChevronRight, IconActivity } from "@tabler/icons-react";
import type { XPEvent } from "@lemonadetraining/contracts";
import { toEventDate } from "../../hooks/useActivityFeed";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";
import EmptyState from "../empty-state/EmptyState";

export interface ActivityFeedProps {
  status: "idle" | "loading" | "ready" | "error";
  items: XPEvent[];
  error?: Error | null;
  /** Pagination: caller-provided callback to fetch the next older page. */
  onLoadMore?: () => Promise<void> | void;
  /** Pagination: when true, the "Load older" affordance is rendered. */
  hasMore?: boolean;
  /** Pagination: while a `loadMore()` is in flight; disables the button. */
  loadingMore?: boolean;
  /** Pagination: most recent loadMore failure, surfaced as inline retry text. */
  loadMoreError?: Error | null;
}

function relativeTime(date: Date | null, now: Date = new Date()): string {
  if (!date) return "";
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

function eventIcon(type: XPEvent["type"]): React.ReactNode {
  switch (type) {
    case "pr":
      return <IconTrophy size={16} aria-hidden="true" />;
    case "badge":
      return <IconTrophy size={16} aria-hidden="true" />;
    case "cascade":
      return <IconChevronRight size={16} aria-hidden="true" />;
    case "workout":
    default:
      return <IconBolt size={16} aria-hidden="true" />;
  }
}

const ActivityFeed: React.FC<ActivityFeedProps> = ({
  status,
  items,
  error,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  loadMoreError = null,
}) => {
  const display = items;
  const now = new Date();
  const reducedMotion = usePrefersReducedMotion();
  // Track ids we've already rendered so newly-prepended events animate exactly
  // once. Mutation is deferred to a post-commit effect so React's StrictMode
  // double-render doesn't mark items as "seen" before they render and silently
  // skip the fade-in.
  const seenIdsRef = useRef<Set<string>>(new Set());
  const newIds = useMemo(() => {
    const seen = seenIdsRef.current;
    if (seen.size === 0) return new Set<string>();
    const fresh = new Set<string>();
    for (const event of display) {
      if (!seen.has(event.id)) fresh.add(event.id);
    }
    return fresh;
  }, [display]);
  useEffect(() => {
    const seen = seenIdsRef.current;
    for (const event of display) seen.add(event.id);
  }, [display]);

  return (
    <Card
      data-testid="activity-feed"
      aria-busy={status === "loading"}
      data-reduced-motion={reducedMotion ? "true" : "false"}
    >
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <IconActivity size={18} aria-hidden="true" />
          Recent activity
        </CardTitle>
      </CardHeader>
      <CardContent>
        {status === "loading" && items.length === 0 ? (
          <div className="flex flex-col gap-2" data-testid="activity-feed-skeletons">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" data-testid="activity-feed-skeleton" />
            ))}
          </div>
        ) : null}
        {status === "error" ? (
          <p role="alert" className="text-sm text-destructive" data-testid="activity-feed-error">
            Couldn't load your latest activity. {error?.message}
          </p>
        ) : null}
        {status === "ready" && items.length === 0 ? (
          <EmptyState message="Every journey starts here" data-testid="activity-feed-empty" />
        ) : null}
        {display.length > 0 ? (
          <ScrollArea
            data-testid="activity-feed-scroll-area"
            viewportClassName="max-h-[60vh]"
            className="pr-2"
          >
            <ul data-testid="activity-feed-list" className="flex flex-col gap-2">
              {display.map((event) => {
                const when = relativeTime(toEventDate(event.timestamp), now);
                const isNew = newIds.has(event.id);
                const animateProps =
                  reducedMotion || !isNew
                    ? { initial: false }
                    : {
                        initial: { opacity: 0 },
                        animate: { opacity: 1 },
                        transition: { duration: 0.3 },
                      };
                return (
                  <motion.li
                    key={event.id}
                    data-testid="activity-feed-item"
                    className="flex items-start gap-2 rounded-md border border-border p-2"
                    {...animateProps}
                  >
                    <span className="mt-0.5 text-muted-foreground">{eventIcon(event.type)}</span>
                    <span className="flex-1 text-sm">
                      <span className="block">{event.description}</span>
                      <span className="block text-xs text-muted-foreground">
                        +{event.xpAwarded} XP {when ? `· ${when}` : ""}
                      </span>
                    </span>
                  </motion.li>
                );
              })}
            </ul>
            {hasMore && onLoadMore ? (
              <div className="mt-2 flex flex-col items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onLoadMore()}
                  disabled={loadingMore}
                  aria-busy={loadingMore}
                  data-testid="activity-feed-load-more"
                >
                  {loadingMore ? "Loading…" : "Load older"}
                </Button>
                {loadMoreError ? (
                  <p
                    role="alert"
                    className="text-xs text-destructive"
                    data-testid="activity-feed-load-more-error"
                  >
                    Couldn't load older activity. Tap to retry.
                  </p>
                ) : null}
              </div>
            ) : null}
          </ScrollArea>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default ActivityFeed;
